import type { FC, ReactElement } from 'react'
import { useEffect, useRef, useState } from 'react'

import { AuthClient } from '@skytech/auth'
import { config } from '@skytech/config'
import { getEnvironmentBadgeLabel } from '@skytech/config/environmentBadge'
import { MessagePopup, Input, InfoBox, Select, LanguageChoiceButtons } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { COUNTRY_FLAGS, countryOptions, getDefaultCountryId } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'
import type { FormikProps, FieldProps } from 'formik'
import { Formik, Form, Field } from 'formik'
import ArrowRightIcon from 'mdi-react/ArrowRightIcon'
import InfoOutlineIcon from 'mdi-react/InfoOutlineIcon'
import { useLocation, useNavigate } from 'react-router-dom'
import { twJoin } from 'tailwind-merge'

import authResources from '@/api/auth'
import queries from '@/api/queries'
import { useAppContext } from '@/App.context'
import { useTranslation } from '@/components/i18n/hooks'
import { Translation } from '@/components/i18n/Translation'
import useLogIn from '@/hooks/useLogin'
import { APP_ROUTES } from '@/util/appRoutes'

import AccessCodeView from './AccessCodeView'
import InternetExplorerWarning from './InternetExplorerWarning'
import PasswordView from './PasswordView'

import './Login.styles.scss'

type FormFields = {
  identifier: string
  phoneCountryId: number
  password?: string
  accessCode?: string
}
type VisibleStep = 'identifier' | 'password' | 'accessCode'

const Login: FC = () => {
  const navigate = useNavigate()
  const translate = useTranslation()
  const { whiteLabel, locale, setLocale } = useAppContext()
  const location = useLocation()

  const searchParams = new URLSearchParams(location.search)
  const nextUrl = searchParams.get('next') || APP_ROUTES.home
  // possible error msg coming from the azure integration
  const azureErrorMessage = searchParams.get('azureErrorMsg')

  const formRef = useRef<FormikProps<FormFields> | null>(null)
  const [visibleStep, setVisibleStep] = useState<VisibleStep>('identifier')
  const [errorPopupMessage, setErrorPopupMessage] = useState<{
    text: string
    title: string
  } | null>(null)

  const loginMutation = useLogIn()

  const {
    data: loginSettings,
    refetch: fetchLoginSettings,
    isInitialLoading: isLoginSettingsLoading,
  } = useQuery(
    queries.loginSettings(),
    () => {
      if (!formRef.current) throw Error('Missing form values')
      return authResources.getSettings({
        phoneNumber: formRef.current?.values.identifier,
        phoneCountryId: formRef.current?.values.phoneCountryId,
      })
    },
    {
      enabled: false,
      staleTime: Infinity,
      onSuccess({ isPasswordRequired, hasMsalLogin }) {
        if (hasMsalLogin) {
          const host = window.location.host
          window.location.replace(
            `${config.AUTH_API_URL}azure-msal/signin/app?host=${host}&pathname=${nextUrl}`
          )
          return
        }

        setVisibleStep(isPasswordRequired ? 'password' : 'accessCode')
      },
      onError() {
        setErrorPopupMessage({
          text: translate('Oops! Noe gikk galt. Vennligst oppdater siden og prøv igjen.'),
          title: translate('Laster inn påloggingsinnstillinger feil'),
        })
      },
    }
  )

  const handleSubmit = (values: FormFields) => {
    const { identifier, password, accessCode, phoneCountryId } = values

    if (visibleStep === 'identifier') {
      fetchLoginSettings()
      return
    }

    if (visibleStep === 'password' && loginSettings?.isAccessCodeRequired) {
      setVisibleStep('accessCode')
      return
    }

    loginMutation.mutate(
      { phoneNumberOrEmail: identifier, password, accessCode, phoneCountryId },
      {
        onSuccess() {
          navigate(nextUrl)
        },
        onError: () => {
          setErrorPopupMessage({
            text: translate('Feil nummer/ kode kombinasjon!'),
            title: translate('Innlogging feil'),
          })
        },
      }
    )
  }

  const handleClickPrev = () => {
    if (visibleStep === 'password') {
      setVisibleStep('identifier')
    }

    if (visibleStep === 'accessCode') {
      if (loginSettings?.isPasswordRequired) {
        setVisibleStep('password')
      } else {
        setVisibleStep('identifier')
      }
    }
  }

  useEffect(() => {
    if (AuthClient.getToken()) {
      navigate('/')
    }
  }, [navigate])

  useEffect(() => {
    if (!azureErrorMessage) return

    setErrorPopupMessage({
      title: translate('Login attempt via Azure failed'),
      text: `${translate('Something went wrong when logging in, please try again!')} ${translate(
        'Error'
      )}: ${azureErrorMessage}`,
    })
  }, [navigate, azureErrorMessage, translate])

  const submitButtonText =
    (visibleStep === 'password' && !loginSettings?.isAccessCodeRequired) ||
    visibleStep === 'accessCode'
      ? translate('Log inn')
      : translate('Gå videre')
  const isSubmitButtonLoading =
    visibleStep === 'identifier' ? isLoginSettingsLoading : loginMutation.isLoading
  const environmentBadgeLabel = getEnvironmentBadgeLabel(config.ENVIRONMENT)
  const defaultPhoneCountryId = getDefaultCountryId(locale)

  return (
    <div className={`login-form-wrapper ${whiteLabel}`}>
      {environmentBadgeLabel && (
        <div className="development-info-container">
          <InfoBox emphasis="warning">
            <InfoOutlineIcon />
            <span className="development-text">
              <Translation
                id={
                  config.ENVIRONMENT === 'demo'
                    ? 'This is a demo version of the app.'
                    : 'This is a development version of the app.'
                }
              />
            </span>
          </InfoBox>
        </div>
      )}

      <div className="login-form">
        <div className="logo" />

        <div>
          <h1 className="text-center md:text-left">
            <Translation id="Welcome to Your Page!" />
          </h1>
        </div>

        <Formik
          innerRef={formRef}
          validateOnBlur={false}
          initialValues={{
            identifier: '',
            phoneCountryId: defaultPhoneCountryId,
            password: '',
            accessCode: '',
          }}
          validate={values => {
            const errors: Partial<Record<keyof FormFields, ReactElement>> = {}

            if (!values.identifier) {
              errors.identifier = <Translation id="Dette feltet er obligatorisk" />
            }

            if (
              !values.password &&
              loginSettings?.isPasswordRequired &&
              visibleStep === 'password'
            ) {
              errors.password = <Translation id="Dette feltet er obligatorisk" />
            }

            if (
              !values.accessCode &&
              loginSettings?.isAccessCodeRequired &&
              visibleStep === 'accessCode'
            ) {
              errors.accessCode = <Translation id="Dette feltet er obligatorisk" />
            }

            return errors
          }}
          onSubmit={handleSubmit}
        >
          {({ values }) => (
            <Form>
              <div className="gap-2 md:flex">
                {visibleStep !== 'identifier' && (
                  <div className="login-info">
                    <div className="sent-code-message">
                      <Translation
                        id="Logger inn som {identifier}"
                        identifier={<span className="identifier">{values.identifier}</span>}
                      />
                    </div>
                  </div>
                )}

                {visibleStep === 'identifier' && (
                  <>
                    <Field name="phoneCountryId">
                      {({ field, form }: FieldProps) => (
                        <Select
                          className="w-fit"
                          id="phoneCountryId"
                          options={countryOptions.map(option => ({
                            value: option.value,
                            label: `${COUNTRY_FLAGS[option.label]} +${option.value}`,
                          }))}
                          marginBottom={16}
                          {...field}
                          onChange={option => {
                            form.setFieldValue(field.name, option?.value)
                            // To preselect phone country code on the login page
                            localStorage.setItem('phoneCountryId', String(option?.value))
                          }}
                        />
                      )}
                    </Field>

                    <Field name="identifier">
                      {({ field, meta }: FieldProps) => (
                        <Input
                          isFullWidth
                          minWidth={300}
                          id="identifier"
                          wrapperClassName="grow"
                          ariaLabel={translate('mobilnummer / epost')}
                          marginBottom={16}
                          touched={meta.touched}
                          error={meta.error}
                          placeholder={translate('Skriv inn mobilnummer / epost')}
                          {...field}
                        />
                      )}
                    </Field>
                  </>
                )}
              </div>

              {visibleStep === 'password' && <PasswordView />}

              {visibleStep === 'accessCode' && (
                <AccessCodeView identifier={values.identifier} countryId={values.phoneCountryId} />
              )}

              <footer className="-mx-2 my-1 flex flex-col justify-between">
                <div
                  className={twJoin(
                    'mobile:flex-row flex flex-col',
                    visibleStep === 'identifier' ? 'justify-end' : 'justify-between'
                  )}
                >
                  {visibleStep !== 'identifier' && (
                    <Button size="medium" onClick={handleClickPrev} variant="tertiary">
                      <Translation id="Tilbake" />
                    </Button>
                  )}

                  <Button
                    isIconRight
                    type="submit"
                    size="medium"
                    data-test="login-button"
                    Icon={<ArrowRightIcon />}
                    // No other ground on which loading could be shown when the redirection to the Azure platform happens
                    isLoading={isSubmitButtonLoading || loginSettings?.hasMsalLogin}
                  >
                    {submitButtonText}
                  </Button>
                </div>

                <LanguageChoiceButtons
                  locale={locale}
                  onChangeLocale={setLocale}
                  className="mobile:justify-end justify-evenly"
                />
              </footer>
            </Form>
          )}
        </Formik>
      </div>

      <MessagePopup
        message={errorPopupMessage?.text}
        title={errorPopupMessage?.title}
        isOpen={Boolean(errorPopupMessage?.text)}
        onClose={() => setErrorPopupMessage(null)}
      />

      <InternetExplorerWarning />
    </div>
  )
}

export default Login
